import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ArrowLeft,
  Clock,
  Timer,
  TrendingUp,
  Coffee,
  Zap,
  AlertTriangle,
  AlertCircle,
  XCircle,
  RefreshCcw,
  Globe,
  Smartphone,
  Monitor,
  ChevronLeft,
  ChevronRight,
  Briefcase,
  Pause,
  Activity,
  ListOrdered,
} from 'lucide-react';
import { todayInTimezone, formatTimeInTimeZone, formatLocalDate } from '@/utils/dateUtils';

const TABS = [
  { id: 'overview', label: 'Overview', icon: Activity },
  { id: 'work', label: 'Work', icon: Briefcase },
  { id: 'breaks', label: 'Breaks', icon: Coffee },
  { id: 'idle', label: 'Idle', icon: Pause },
  { id: 'timeline', label: 'Timeline', icon: ListOrdered },
];

const DeviceIcon = ({ deviceType, className = 'w-3.5 h-3.5' }) => {
  switch (deviceType) {
    case 1:
      return <Globe className={`${className} text-sky-500`} title="Browser" />;
    case 2:
      return <Smartphone className={`${className} text-indigo-500`} title="Mobile" />;
    case 3:
      return <Monitor className={`${className} text-violet-500`} title="Desktop" />;
    default:
      return null;
  }
};

function formatDurationMinutes(minutes) {
  const m = Number(minutes || 0);
  if (!m || m <= 0) return '—';
  const totalSeconds = Math.round(m * 60);
  const hrs = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  if (hrs > 0) return secs > 0 ? `${hrs}h ${mins}m ${secs}s` : `${hrs}h ${mins}m`;
  if (mins > 0) return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  return `${secs}s`;
}

function formatHoursLabel(hours) {
  const h = Number(hours || 0);
  if (!h) return '0h';
  if (h < 1) return formatDurationMinutes(h * 60);
  return `${h}h`;
}

function getBreakMinutes(brk) {
  if (!brk) return 0;
  if (brk.duration_minutes != null && brk.duration_minutes > 0) return Number(brk.duration_minutes);
  if (brk.duration_seconds != null && brk.duration_seconds > 0) return Number(brk.duration_seconds) / 60;
  if (brk.start_time) {
    try {
      const start = new Date(brk.start_time);
      const end = brk.end_time ? new Date(brk.end_time) : new Date();
      return Math.max(0, (end - start) / 60000);
    } catch {
      return 0;
    }
  }
  return Number(brk.duration || 0);
}

function isForceBreak(brk) {
  const bt = brk?.break_type;
  return bt === 'force' || bt === 'manual_force' || brk?.is_force_break === true;
}

function StaffAttendanceDetailPage() {
  const { staffId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const pathTab = location.pathname.includes('/work/')
    ? 'work'
    : location.pathname.includes('/breaks/')
      ? 'breaks'
      : location.pathname.includes('/idle/')
        ? 'idle'
        : null;

  const initialTab = searchParams.get('tab') || pathTab || 'overview';
  const dateParam = searchParams.get('date');
  const levelParam = searchParams.get('level');

  const [tab, setTab] = useState(
    TABS.some((t) => t.id === initialTab) ? initialTab : 'overview'
  );
  const [selectedDate, setSelectedDate] = useState(dateParam || '');
  const [companyTimezone, setCompanyTimezone] = useState('UTC');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [work, setWork] = useState(null);
  const [breaks, setBreaks] = useState(null);
  const [idle, setIdle] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [breakFilter, setBreakFilter] = useState('all');
  const [idleFilter, setIdleFilter] = useState(levelParam || 'all');
  const tickRef = useRef(0);
  const [, setTick] = useState(0);

  useEffect(() => {
    axios
      .get(`${API}/admin/settings/company`, { withCredentials: true })
      .then((res) => setCompanyTimezone(res.data?.timezone || 'UTC'))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedDate && companyTimezone) {
      setSelectedDate(dateParam || todayInTimezone(companyTimezone));
    }
  }, [companyTimezone, dateParam, selectedDate]);

  const targetDate = selectedDate || dateParam || todayInTimezone(companyTimezone);
  const isToday = targetDate === todayInTimezone(companyTimezone);
  const staffTz =
    work?.timezone || breaks?.timezone || idle?.timezone || companyTimezone || 'UTC';
  const staffName = work?.staff_name || breaks?.staff_name || idle?.staff_name || 'Staff';
  const department = work?.department || breaks?.department || idle?.department;

  const syncUrl = useCallback(
    (nextTab, nextDate, nextLevel) => {
      const params = new URLSearchParams();
      params.set('tab', nextTab || tab);
      params.set('date', nextDate || targetDate);
      if (nextLevel && nextLevel !== 'all') params.set('level', nextLevel);
      const qs = params.toString();
      const detailPath = `/admin/attendance/detail/${staffId}`;
      if (!location.pathname.includes('/detail/')) {
        navigate(`${detailPath}?${qs}`, { replace: true });
      } else {
        setSearchParams(params, { replace: true });
      }
    },
    [setSearchParams, tab, targetDate, staffId, location.pathname, navigate]
  );

  const fetchAll = useCallback(
    async (silent = false) => {
      if (!staffId || !targetDate) return;
      try {
        if (!silent) setLoading(true);
        else setRefreshing(true);
        setError('');
        const q = `from_date=${targetDate}&to_date=${targetDate}`;
        const [workRes, breakRes, idleRes] = await Promise.all([
          axios.get(`${API}/admin/staff/${staffId}/work-stats?${q}`, { withCredentials: true }),
          axios.get(`${API}/admin/staff/${staffId}/break-stats?${q}`, { withCredentials: true }),
          axios.get(`${API}/admin/staff/${staffId}/idle-stats?${q}`, { withCredentials: true }),
        ]);
        setWork(workRes.data);
        setBreaks(breakRes.data);
        setIdle(idleRes.data);
        setLastUpdate(new Date());
      } catch (err) {
        console.error('Failed to load attendance detail:', err);
        if (!silent) {
          setError(err.response?.data?.detail || 'Failed to load attendance details');
        }
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [staffId, targetDate]
  );

  useEffect(() => {
    fetchAll(false);
  }, [fetchAll]);

  // Live refresh for today
  useEffect(() => {
    if (!isToday) return undefined;
    const id = setInterval(() => fetchAll(true), 5000);
    return () => clearInterval(id);
  }, [isToday, fetchAll]);

  // Keep open-break / in-progress timers fresh
  useEffect(() => {
    if (!isToday) return undefined;
    const id = setInterval(() => {
      tickRef.current += 1;
      setTick(tickRef.current);
    }, 1000);
    return () => clearInterval(id);
  }, [isToday]);

  const changeTab = (next) => {
    setTab(next);
    syncUrl(next, targetDate, idleFilter);
  };

  const changeDate = (next) => {
    setSelectedDate(next);
    syncUrl(tab, next, idleFilter);
  };

  const shiftDate = (delta) => {
    const [y, m, d] = targetDate.split('-').map(Number);
    const next = new Date(Date.UTC(y, m - 1, d + delta)).toISOString().split('T')[0];
    if (delta > 0 && next > todayInTimezone(companyTimezone)) return;
    changeDate(next);
  };

  const fmtTime = (iso, withSeconds = false) => {
    if (!iso) return '—';
    if (withSeconds) {
      try {
        return new Intl.DateTimeFormat('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          second: '2-digit',
          hour12: true,
          timeZone: staffTz,
        }).format(new Date(iso));
      } catch {
        return formatTimeInTimeZone(iso, staffTz);
      }
    }
    return formatTimeInTimeZone(iso, staffTz);
  };

  const workSummary = work?.summary || {};
  const breakSummary = breaks?.summary || {};
  const idleSummary = idle?.summary || {};
  const sessions = work?.sessions || [];
  const breakList = breaks?.breaks || [];
  const idleList = idle?.idle_reports || [];

  const filteredBreaks = useMemo(() => {
    return breakList.filter((b) => {
      if (breakFilter === 'all') return true;
      if (breakFilter === 'force') return isForceBreak(b);
      return !isForceBreak(b);
    });
  }, [breakList, breakFilter]);

  const filteredIdle = useMemo(() => {
    return idleList.filter((r) => {
      if (idleFilter === 'all') return true;
      return r.idle_level === parseInt(idleFilter, 10);
    });
  }, [idleList, idleFilter]);

  const openBreak = breakList.find((b) => b.is_open || !b.end_time);
  const openSession = sessions.find((s) => s.check_in && !s.check_out);
  const lateSession = sessions.find((s) => (s.late_by || 0) > 0 || s.status === 'late');

  const timeline = useMemo(() => {
    const events = [];
    sessions.forEach((s, i) => {
      if (s.check_in) {
        events.push({
          id: `in-${i}`,
          at: s.check_in,
          kind: 'check_in',
          title: `Check in · Session ${s.session_number || i + 1}`,
          meta: s.status,
          tone: 'indigo',
          device: s.check_in_device,
        });
      }
      if (s.check_out) {
        events.push({
          id: `out-${i}`,
          at: s.check_out,
          kind: 'check_out',
          title: `Check out · Session ${s.session_number || i + 1}`,
          meta: formatDurationMinutes(s.net_work_minutes),
          tone: 'rose',
          device: s.check_out_device,
        });
      }
    });
    breakList.forEach((b, i) => {
      events.push({
        id: `brk-${i}`,
        at: b.start_time,
        kind: 'break',
        title: isForceBreak(b) ? 'Force break' : 'Manual break',
        meta: formatDurationMinutes(getBreakMinutes(b)),
        tone: isForceBreak(b) ? 'rose' : 'orange',
        ongoing: b.is_open || !b.end_time,
      });
    });
    idleList.forEach((r, i) => {
      events.push({
        id: `idle-${i}`,
        at: r.timestamp,
        kind: 'idle',
        title: `Idle Level ${r.idle_level}`,
        meta: formatDurationMinutes(r.idle_minutes),
        tone: r.idle_level === 3 ? 'rose' : r.idle_level === 2 ? 'orange' : 'amber',
      });
    });
    return events
      .filter((e) => e.at)
      .sort((a, b) => new Date(a.at) - new Date(b.at));
  }, [sessions, breakList, idleList]);

  const dateLabel = useMemo(() => {
    try {
      const [y, m, d] = targetDate.split('-').map(Number);
      return new Date(y, m - 1, d).toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return targetDate;
    }
  }, [targetDate]);

  const kpiCards = [
    {
      title: 'Work',
      value: formatHoursLabel(workSummary.total_work_hours),
      sub: `${workSummary.total_sessions || 0} sessions`,
      icon: Timer,
      accent: 'from-indigo-500 to-purple-600',
      soft: 'bg-indigo-50 text-indigo-600',
      tab: 'work',
    },
    {
      title: 'Breaks',
      value: formatDurationMinutes(breakSummary.total_break_minutes ?? (breakSummary.total_break_hours || 0) * 60),
      sub: `${breakSummary.total_breaks || 0} breaks`,
      icon: Coffee,
      accent: 'from-orange-500 to-amber-500',
      soft: 'bg-orange-50 text-orange-600',
      tab: 'breaks',
    },
    {
      title: 'Idle',
      value: formatHoursLabel(idleSummary.total_idle_hours),
      sub: `${(idleSummary.idle_level_1_count || 0) + (idleSummary.idle_level_2_count || 0) + (idleSummary.idle_level_3_count || 0)} events`,
      icon: Pause,
      accent: 'from-amber-500 to-rose-500',
      soft: 'bg-amber-50 text-amber-700',
      tab: 'idle',
    },
    {
      title: 'Overtime',
      value: formatHoursLabel(workSummary.total_overtime_hours),
      sub: lateSession ? `Late ${lateSession.late_by || 0}m` : 'On time',
      icon: TrendingUp,
      accent: 'from-violet-500 to-fuchsia-500',
      soft: 'bg-violet-50 text-violet-600',
      tab: 'work',
    },
  ];

  if (loading && !work && !breaks && !idle) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
      </div>
    );
  }

  if (error && !work && !breaks && !idle) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">{error}</div>
      </div>
    );
  }

  return (
    <div className="relative pb-8" data-testid="staff-attendance-detail">
      <div className="pointer-events-none absolute inset-x-0 -top-6 h-56 bg-[radial-gradient(ellipse_at_top,_rgba(99,102,241,0.12),_transparent_60%)]" />

      <div className="relative space-y-5 md:space-y-6">
        {/* Hero */}
        <section className="att-fade-up overflow-hidden rounded-3xl border border-white/70 bg-white/90 shadow-[0_20px_50px_-28px_rgba(15,23,42,0.35)] backdrop-blur">
          <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-4 py-4 text-white sm:px-6 sm:py-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-start gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate('/admin/attendance')}
                  className="mt-0.5 h-10 w-10 shrink-0 rounded-2xl bg-white/15 text-white ring-1 ring-white/25 hover:bg-white/25"
                  data-testid="back-btn"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2.5">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/20 text-lg font-bold shadow-inner backdrop-blur">
                      {(staffName || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <h1 className="truncate text-2xl font-bold tracking-tight sm:text-3xl">
                        {staffName}
                      </h1>
                      <p className="mt-0.5 text-sm text-indigo-50/90">
                        Attendance detail
                        {department ? ` · ${department}` : ''}
                      </p>
                    </div>
                    {isToday && (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-2.5 py-1 text-xs font-semibold backdrop-blur">
                        <span className="h-2 w-2 rounded-full bg-white att-live-dot" />
                        Live
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div className="inline-flex items-center rounded-2xl bg-white/15 p-1 ring-1 ring-white/25 backdrop-blur">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => shiftDate(-1)}
                    className="h-9 w-9 rounded-xl text-white hover:bg-white/20"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <input
                    type="date"
                    value={targetDate}
                    onChange={(e) => changeDate(e.target.value)}
                    className="h-9 w-[9.75rem] rounded-xl border-0 bg-transparent px-1 text-sm font-semibold text-white focus:outline-none [color-scheme:dark]"
                    data-testid="detail-date-picker"
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={isToday}
                    onClick={() => shiftDate(1)}
                    className="h-9 w-9 rounded-xl text-white hover:bg-white/20 disabled:opacity-40"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-11 w-11 rounded-2xl bg-white/15 text-white ring-1 ring-white/25 hover:bg-white/25"
                  onClick={() => fetchAll(false)}
                  data-testid="detail-refresh"
                >
                  <RefreshCcw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                </Button>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-slate-50/70 px-4 py-3 sm:px-6">
            <p className="text-sm font-medium text-slate-600">{dateLabel}</p>
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 font-medium text-slate-700 ring-1 ring-slate-200">
                <Globe className="h-3 w-3 text-purple-600" /> {staffTz}
              </span>
              {work?.shift_start && (
                <span className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
                  <Clock className="h-3 w-3" /> Shift {work.shift_start}–{work.shift_end}
                </span>
              )}
              {lastUpdate && <span>Updated {lastUpdate.toLocaleTimeString()}</span>}
            </div>
          </div>

          {/* Live status strip */}
          {(openSession || openBreak || lateSession) && (
            <div className="flex flex-wrap gap-2 px-4 py-3 sm:px-6">
              {openSession && (
                <div className="inline-flex items-center gap-2 rounded-2xl bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 ring-1 ring-indigo-100">
                  <span className="h-2 w-2 rounded-full bg-indigo-500 att-live-dot" />
                  Working since {fmtTime(openSession.check_in)}
                </div>
              )}
              {openBreak && (
                <div className="inline-flex items-center gap-2 rounded-2xl bg-orange-50 px-3 py-2 text-sm font-semibold text-orange-700 ring-1 ring-orange-100">
                  <span className="h-2 w-2 rounded-full bg-orange-500 att-live-dot" />
                  {isForceBreak(openBreak) ? 'Force break' : 'On break'} ·{' '}
                  <span className="att-timer-live tabular-nums">
                    {formatDurationMinutes(getBreakMinutes(openBreak))}
                  </span>
                </div>
              )}
              {lateSession && (
                <div className="inline-flex items-center gap-2 rounded-2xl bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-800 ring-1 ring-amber-100">
                  Late by {lateSession.late_by || 0}m
                </div>
              )}
            </div>
          )}
        </section>

        {/* KPI grid */}
        <section className="att-fade-up grid grid-cols-2 gap-3 lg:grid-cols-4 md:gap-4" style={{ animationDelay: '60ms' }}>
          {kpiCards.map((k) => (
            <button
              key={k.title}
              type="button"
              onClick={() => changeTab(k.tab)}
              className="group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-4 text-left shadow-sm transition duration-300 hover:-translate-y-0.5 hover:shadow-lg"
              data-testid={`detail-kpi-${k.title.toLowerCase()}`}
            >
              <div className={`absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br ${k.accent} opacity-10 blur-xl transition group-hover:opacity-20`} />
              <div className="relative flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{k.title}</p>
                  <p className="mt-2 text-2xl font-bold tabular-nums tracking-tight text-slate-900 sm:text-3xl">
                    {k.value}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">{k.sub}</p>
                </div>
                <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${k.soft}`}>
                  <k.icon className="h-5 w-5" />
                </div>
              </div>
            </button>
          ))}
        </section>

        {/* Tabs */}
        <section className="att-fade-up space-y-4" style={{ animationDelay: '120ms' }}>
          <div className="overflow-x-auto">
            <div
              className="inline-flex min-w-full gap-1 rounded-2xl bg-slate-100/90 p-1.5 sm:min-w-0"
              role="tablist"
            >
              {TABS.map((t) => {
                const active = tab === t.id;
                const Icon = t.icon;
                return (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => changeTab(t.id)}
                    className={`inline-flex flex-1 items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold transition ${
                      active
                        ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200/80'
                        : 'text-slate-500 hover:text-slate-800'
                    }`}
                    data-testid={`detail-tab-${t.id}`}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="whitespace-nowrap">{t.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* OVERVIEW */}
          {tab === 'overview' && (
            <div className="grid gap-4 lg:grid-cols-3">
              <div className="space-y-4 lg:col-span-2">
                <div className="rounded-3xl border border-slate-200/70 bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-slate-400">Day snapshot</h3>
                  <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {[
                      { label: 'Net work', value: formatHoursLabel(workSummary.total_work_hours), cls: 'text-indigo-600' },
                      { label: 'Break time', value: formatDurationMinutes(breakSummary.total_break_minutes), cls: 'text-orange-600' },
                      { label: 'Idle time', value: formatHoursLabel(idleSummary.total_idle_hours), cls: 'text-amber-700' },
                      { label: 'Force breaks', value: String(breakSummary.total_force_breaks || 0), cls: 'text-rose-600' },
                      { label: 'Idle L3', value: String(idleSummary.idle_level_3_count || 0), cls: 'text-rose-600' },
                      { label: 'Sessions', value: String(workSummary.total_sessions || 0), cls: 'text-slate-900' },
                    ].map((item) => (
                      <div key={item.label} className="rounded-2xl bg-slate-50 px-3.5 py-3 ring-1 ring-slate-100">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{item.label}</p>
                        <p className={`mt-1 text-xl font-bold tabular-nums ${item.cls}`}>{item.value}</p>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-3xl border border-slate-200/70 bg-white p-5 shadow-sm">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold uppercase tracking-wider text-slate-400">Recent activity</h3>
                    <button
                      type="button"
                      onClick={() => changeTab('timeline')}
                      className="text-xs font-semibold text-purple-700 hover:underline"
                    >
                      Full timeline
                    </button>
                  </div>
                  <div className="mt-4 space-y-3">
                    {timeline.length === 0 ? (
                      <p className="py-8 text-center text-sm text-slate-400">No activity for this day</p>
                    ) : (
                      timeline.slice(-6).reverse().map((ev) => (
                        <div key={ev.id} className="flex items-start gap-3">
                          <div
                            className={`mt-1 h-2.5 w-2.5 shrink-0 rounded-full ${
                              ev.tone === 'indigo'
                                ? 'bg-indigo-500'
                                : ev.tone === 'rose'
                                  ? 'bg-rose-500'
                                  : ev.tone === 'orange'
                                    ? 'bg-orange-500'
                                    : 'bg-amber-500'
                            }`}
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <p className="text-sm font-semibold text-slate-900">{ev.title}</p>
                              <p className="text-xs tabular-nums text-slate-400">{fmtTime(ev.at)}</p>
                            </div>
                            <p className="text-xs text-slate-500">
                              {ev.meta}
                              {ev.ongoing ? ' · ongoing' : ''}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="rounded-3xl border border-slate-200/70 bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-slate-400">Idle levels</h3>
                  <div className="mt-4 space-y-3">
                    {[
                      { n: 1, hours: idleSummary.idle_level_1_hours, count: idleSummary.idle_level_1_count, cls: 'bg-amber-100 text-amber-800', icon: AlertTriangle },
                      { n: 2, hours: idleSummary.idle_level_2_hours, count: idleSummary.idle_level_2_count, cls: 'bg-orange-100 text-orange-800', icon: AlertCircle },
                      { n: 3, hours: idleSummary.idle_level_3_hours, count: idleSummary.idle_level_3_count, cls: 'bg-rose-100 text-rose-800', icon: XCircle },
                    ].map((lvl) => (
                      <button
                        key={lvl.n}
                        type="button"
                        onClick={() => {
                          setIdleFilter(String(lvl.n));
                          changeTab('idle');
                          syncUrl('idle', targetDate, String(lvl.n));
                        }}
                        className="flex w-full items-center justify-between rounded-2xl bg-slate-50 px-3.5 py-3 text-left ring-1 ring-slate-100 transition hover:bg-white hover:shadow-sm"
                      >
                        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold ${lvl.cls}`}>
                          <lvl.icon className="h-3.5 w-3.5" /> L{lvl.n}
                        </span>
                        <span className="text-right">
                          <span className="block text-sm font-bold tabular-nums text-slate-900">
                            {formatHoursLabel(lvl.hours)}
                          </span>
                          <span className="text-[11px] text-slate-400">{lvl.count || 0}×</span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="rounded-3xl border border-slate-200/70 bg-white p-5 shadow-sm">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-slate-400">Break mix</h3>
                  <div className="mt-4 space-y-3">
                    <div className="flex items-center justify-between rounded-2xl bg-sky-50 px-3.5 py-3 ring-1 ring-sky-100">
                      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-sky-800">
                        <Coffee className="h-4 w-4" /> Manual
                      </span>
                      <span className="text-sm font-bold tabular-nums text-sky-900">
                        {formatDurationMinutes(breakSummary.manual_break_minutes)} · {breakSummary.total_manual_breaks || 0}
                      </span>
                    </div>
                    <div className="flex items-center justify-between rounded-2xl bg-rose-50 px-3.5 py-3 ring-1 ring-rose-100">
                      <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-rose-800">
                        <Zap className="h-4 w-4" /> Force
                      </span>
                      <span className="text-sm font-bold tabular-nums text-rose-900">
                        {formatDurationMinutes(breakSummary.force_break_minutes)} · {breakSummary.total_force_breaks || 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* WORK */}
          {tab === 'work' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {[
                  { label: 'Total work', value: formatHoursLabel(workSummary.total_work_hours), icon: Timer, soft: 'bg-indigo-50 text-indigo-600' },
                  { label: 'Overtime', value: formatHoursLabel(workSummary.total_overtime_hours), icon: TrendingUp, soft: 'bg-violet-50 text-violet-600' },
                  { label: 'Break deducted', value: formatHoursLabel(workSummary.total_break_hours), icon: Clock, soft: 'bg-orange-50 text-orange-600' },
                  { label: 'Avg / day', value: formatHoursLabel(workSummary.avg_work_hours_per_day), icon: Activity, soft: 'bg-sky-50 text-sky-600' },
                ].map((c) => (
                  <div key={c.label} className="rounded-2xl border border-slate-200/70 bg-white p-4 shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${c.soft}`}>
                        <c.icon className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{c.label}</p>
                        <p className="text-xl font-bold tabular-nums text-slate-900">{c.value}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="overflow-hidden rounded-3xl border border-slate-200/70 bg-white shadow-sm">
                <div className="border-b border-slate-100 px-5 py-3">
                  <h3 className="font-bold text-slate-900">Sessions</h3>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50/80">
                        <TableHead>Session</TableHead>
                        <TableHead>Check in</TableHead>
                        <TableHead>Check out</TableHead>
                        <TableHead>Gross</TableHead>
                        <TableHead>Breaks</TableHead>
                        <TableHead>Net</TableHead>
                        <TableHead>OT</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sessions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="py-12 text-center text-slate-400">
                            No sessions for this day
                          </TableCell>
                        </TableRow>
                      ) : (
                        sessions.map((session, index) => (
                          <TableRow key={session.attendance_id || index} data-testid={`session-row-${index}`}>
                            <TableCell>
                              <span className="rounded-lg bg-indigo-50 px-2 py-1 text-xs font-bold text-indigo-700">
                                #{session.session_number || index + 1}
                              </span>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1.5 font-semibold text-indigo-600">
                                <DeviceIcon deviceType={session.check_in_device} />
                                {fmtTime(session.check_in)}
                              </div>
                            </TableCell>
                            <TableCell>
                              {session.check_out ? (
                                <div className="flex items-center gap-1.5 font-semibold text-rose-600">
                                  <DeviceIcon deviceType={session.check_out_device} />
                                  {fmtTime(session.check_out)}
                                </div>
                              ) : (
                                <span className="italic text-slate-400">In progress</span>
                              )}
                            </TableCell>
                            <TableCell className="tabular-nums">{formatDurationMinutes(session.gross_work_minutes)}</TableCell>
                            <TableCell className="tabular-nums text-orange-600">
                              {session.break_minutes > 0
                                ? `−${formatDurationMinutes(session.break_minutes)} (${session.breaks_count})`
                                : '—'}
                            </TableCell>
                            <TableCell className="font-semibold tabular-nums text-indigo-600">
                              {formatDurationMinutes(session.net_work_minutes)}
                            </TableCell>
                            <TableCell className="tabular-nums text-violet-600">
                              {session.overtime_minutes > 0
                                ? `+${formatDurationMinutes(session.overtime_minutes)}`
                                : '—'}
                            </TableCell>
                            <TableCell>
                              <span
                                className={`rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${
                                  session.status === 'present'
                                    ? 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100'
                                    : session.status === 'late'
                                      ? 'bg-amber-50 text-amber-800 ring-1 ring-amber-100'
                                      : 'bg-slate-100 text-slate-600'
                                }`}
                              >
                                {session.status}
                                {session.late_by > 0 ? ` · ${session.late_by}m` : ''}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}

          {/* BREAKS */}
          {tab === 'breaks' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {[
                  { label: 'Total break', value: formatDurationMinutes(breakSummary.total_break_minutes), sub: `${breakSummary.total_breaks || 0} breaks`, soft: 'bg-orange-50 text-orange-600', icon: Timer },
                  { label: 'Manual', value: formatDurationMinutes(breakSummary.manual_break_minutes), sub: `${breakSummary.total_manual_breaks || 0}`, soft: 'bg-sky-50 text-sky-600', icon: Coffee },
                  { label: 'Force', value: formatDurationMinutes(breakSummary.force_break_minutes), sub: `${breakSummary.total_force_breaks || 0}`, soft: 'bg-rose-50 text-rose-600', icon: Zap },
                  {
                    label: 'Avg length',
                    value:
                      breakSummary.total_breaks > 0
                        ? formatDurationMinutes(
                            (breakSummary.total_break_minutes || 0) / breakSummary.total_breaks
                          )
                        : '—',
                    sub: 'per break',
                    soft: 'bg-slate-100 text-slate-600',
                    icon: Clock,
                  },
                ].map((c) => (
                  <div key={c.label} className="rounded-2xl border border-slate-200/70 bg-white p-4 shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${c.soft}`}>
                        <c.icon className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{c.label}</p>
                        <p className="text-xl font-bold tabular-nums text-slate-900">{c.value}</p>
                        <p className="text-[11px] text-slate-400">{c.sub}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'all', label: `All (${breakSummary.total_breaks || 0})` },
                  { id: 'manual', label: `Manual (${breakSummary.total_manual_breaks || 0})` },
                  { id: 'force', label: `Force (${breakSummary.total_force_breaks || 0})` },
                ].map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => setBreakFilter(f.id)}
                    className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition ${
                      breakFilter === f.id
                        ? 'bg-slate-900 text-white shadow-sm'
                        : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              <div className="overflow-hidden rounded-3xl border border-slate-200/70 bg-white shadow-sm">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50/80">
                        <TableHead>Start</TableHead>
                        <TableHead>End</TableHead>
                        <TableHead>Duration</TableHead>
                        <TableHead>Type</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredBreaks.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="py-12 text-center text-slate-400">
                            No breaks for this day
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredBreaks.map((brk, index) => (
                          <TableRow key={index} data-testid={`break-row-${index}`}>
                            <TableCell className="font-semibold tabular-nums">
                              {fmtTime(brk.start_time, true)}
                            </TableCell>
                            <TableCell>
                              {brk.is_open || !brk.end_time ? (
                                <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-800 ring-1 ring-amber-100">
                                  Ongoing
                                </span>
                              ) : (
                                <span className="tabular-nums">{fmtTime(brk.end_time, true)}</span>
                              )}
                            </TableCell>
                            <TableCell className="font-semibold tabular-nums">
                              {formatDurationMinutes(getBreakMinutes(brk))}
                            </TableCell>
                            <TableCell>
                              {isForceBreak(brk) ? (
                                <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-1 text-xs font-bold text-rose-700 ring-1 ring-rose-100">
                                  <Zap className="h-3 w-3" /> Force
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2.5 py-1 text-xs font-bold text-sky-700 ring-1 ring-sky-100">
                                  <Coffee className="h-3 w-3" /> Manual
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}

          {/* IDLE */}
          {tab === 'idle' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                {[
                  { label: 'Total idle', value: formatHoursLabel(idleSummary.total_idle_hours), soft: 'bg-sky-50 text-sky-600', icon: Timer },
                  { label: 'Level 1', value: formatHoursLabel(idleSummary.idle_level_1_hours), sub: `${idleSummary.idle_level_1_count || 0}×`, soft: 'bg-amber-50 text-amber-700', icon: AlertTriangle },
                  { label: 'Level 2', value: formatHoursLabel(idleSummary.idle_level_2_hours), sub: `${idleSummary.idle_level_2_count || 0}×`, soft: 'bg-orange-50 text-orange-700', icon: AlertCircle },
                  { label: 'Level 3', value: formatHoursLabel(idleSummary.idle_level_3_hours), sub: `${idleSummary.idle_level_3_count || 0}×`, soft: 'bg-rose-50 text-rose-700', icon: XCircle },
                ].map((c) => (
                  <div key={c.label} className="rounded-2xl border border-slate-200/70 bg-white p-4 shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${c.soft}`}>
                        <c.icon className="h-5 w-5" />
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{c.label}</p>
                        <p className="text-xl font-bold tabular-nums text-slate-900">{c.value}</p>
                        {c.sub && <p className="text-[11px] text-slate-400">{c.sub}</p>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'all', label: 'All' },
                  { id: '1', label: 'Level 1' },
                  { id: '2', label: 'Level 2' },
                  { id: '3', label: 'Level 3' },
                ].map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={() => {
                      setIdleFilter(f.id);
                      syncUrl('idle', targetDate, f.id);
                    }}
                    className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition ${
                      idleFilter === f.id
                        ? 'bg-slate-900 text-white shadow-sm'
                        : 'bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              <div className="overflow-hidden rounded-3xl border border-slate-200/70 bg-white shadow-sm">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50/80">
                        <TableHead>Date</TableHead>
                        <TableHead>Time</TableHead>
                        <TableHead>Duration</TableHead>
                        <TableHead>Level</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredIdle.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="py-12 text-center text-slate-400">
                            No idle reports for this day
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredIdle.map((report, index) => (
                          <TableRow key={index} data-testid={`idle-row-${index}`}>
                            <TableCell className="font-medium">
                              {formatLocalDate(report.date || report.timestamp)}
                            </TableCell>
                            <TableCell className="tabular-nums">{fmtTime(report.timestamp)}</TableCell>
                            <TableCell className="font-semibold tabular-nums">
                              {formatDurationMinutes(report.idle_minutes)}
                            </TableCell>
                            <TableCell>
                              {report.idle_level === 1 && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-800 ring-1 ring-amber-100">
                                  <AlertTriangle className="h-3 w-3" /> Level 1
                                </span>
                              )}
                              {report.idle_level === 2 && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-orange-50 px-2.5 py-1 text-xs font-bold text-orange-800 ring-1 ring-orange-100">
                                  <AlertCircle className="h-3 w-3" /> Level 2
                                </span>
                              )}
                              {report.idle_level === 3 && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-1 text-xs font-bold text-rose-800 ring-1 ring-rose-100">
                                  <XCircle className="h-3 w-3" /> Level 3
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}

          {/* TIMELINE */}
          {tab === 'timeline' && (
            <div className="rounded-3xl border border-slate-200/70 bg-white p-5 shadow-sm sm:p-6">
              <h3 className="text-lg font-bold text-slate-900">Day timeline</h3>
              <p className="mt-1 text-sm text-slate-500">
                Check-ins, breaks, and idle events in staff timezone ({staffTz})
              </p>
              {timeline.length === 0 ? (
                <p className="py-16 text-center text-slate-400">No events for this day</p>
              ) : (
                <ol className="relative mt-6 space-y-0 border-l-2 border-slate-100 pl-6">
                  {timeline.map((ev) => (
                    <li key={ev.id} className="relative pb-6 last:pb-0">
                      <span
                        className={`absolute -left-[1.91rem] top-1 flex h-5 w-5 items-center justify-center rounded-full ring-4 ring-white ${
                          ev.tone === 'indigo'
                            ? 'bg-indigo-500'
                            : ev.tone === 'rose'
                              ? 'bg-rose-500'
                              : ev.tone === 'orange'
                                ? 'bg-orange-500'
                                : 'bg-amber-500'
                        }`}
                      />
                      <div className="rounded-2xl bg-slate-50/80 px-4 py-3 ring-1 ring-slate-100">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="font-semibold text-slate-900">{ev.title}</p>
                          <p className="text-xs font-medium tabular-nums text-slate-400">
                            {fmtTime(ev.at, true)}
                          </p>
                        </div>
                        <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-slate-500">
                          {ev.device != null && <DeviceIcon deviceType={ev.device} />}
                          <span>{ev.meta}</span>
                          {ev.ongoing && (
                            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-800">
                              Ongoing
                            </span>
                          )}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          )}
        </section>

        {isToday && (
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-4 py-2 text-sm font-medium text-indigo-700 shadow-sm ring-1 ring-indigo-100">
              <span className="h-2 w-2 rounded-full bg-indigo-500 att-live-dot" />
              Live refresh every 5 seconds
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default StaffAttendanceDetailPage;
